import { AsyncState, Fun, stateful, unloadedAsyncState, Widget } from "widgets-for-react";
import { fromPromiseValidator, PromiseValidator, Validator, ValidatorResult, validatorResultBuilders, yesValidator } from "./validatorExpr";
import { phoneNumberRegex } from "../utils/email-and-phone.regex";
export * from "./validatorExpr";

export interface BaseValidatorBuilder<state,t,key extends string | number | symbol> {
  fromPromise(p:PromiseValidator<state,t>) : Validator<state,t>,
  key:key,
}

export interface StringValidatorBuilder<state,key extends string | number | symbol> extends BaseValidatorBuilder<state,string | undefined,key>{
  matches(r:RegExp) : Validator<state,string | undefined>,
  notEmpty(): Validator<state,string | undefined>,
  isPhone(): Validator<state,string | undefined>,
  isEmail(): Validator<state,string | undefined>,
  isPostalCode(): Validator<state, string | undefined>,
  isNumeric(): Validator<state, string | undefined>,
  eq(v:string) : Validator<state,string | undefined>  
  ne(v:string) : Validator<state,string | undefined>  
}

export interface DateValidatorBuilder<state,key extends string | number | symbol> extends BaseValidatorBuilder<state,Date | undefined,key>{
  gt(v:Date) : Validator<state,Date | undefined>  
  eq(v:Date) : Validator<state,Date | undefined>  
  ne(v:Date) : Validator<state,Date | undefined>  
  lt(v:Date) : Validator<state,Date | undefined>  
}

export interface NumberValidatorBuilder<state,key extends string | number | symbol> extends BaseValidatorBuilder<state,number | undefined,key>{
  eq(v:number) : Validator<state,number | undefined>
  gt(v:number) : Validator<state,number | undefined>
  ne(v:number) : Validator<state,number | undefined>
  lt(v:number) : Validator<state,number | undefined>
}

export interface BoolValidatorBuilder<state,key extends string | number | symbol> extends BaseValidatorBuilder<state,boolean | undefined,key>{
  isTrue() : Validator<state,boolean | undefined>
  isFalse() : Validator<state,boolean | undefined>
  eq(v:boolean) : Validator<state,boolean | undefined>
  ne(v:boolean) : Validator<state,boolean | undefined>
}

export interface ObjectValidatorBuilder<state,t extends Object,key extends string | number | symbol> extends BaseValidatorBuilder<state,t,key> {
  fields(b:Partial<FieldValidatorsBuilder<state,t>>) : Validator<state,t>
}

export interface GenericValidatorBuilder<state,t extends Object,key extends string | number | symbol> extends BaseValidatorBuilder<state,t,key> {}

export type ValidatorBuilder<state,t,key extends string | number | symbol> =
  t extends string ? StringValidatorBuilder<state,key> :
  t extends number ? NumberValidatorBuilder<state,key> :
  t extends boolean ? BoolValidatorBuilder<state,key> :
  t extends Date ? DateValidatorBuilder<state,key> :
  t extends Object ? ObjectValidatorBuilder<state,t,key> :
  // t extends undefined ? (StringValidatorBuilder<state,key> & NumberValidatorBuilder<state,key> & BoolValidatorBuilder<state,key> & DateValidatorBuilder<state,key> & ObjectValidatorBuilder<state,t,key>) 
  // : GenericValidatorBuilder<state,t,key>
  never

export type ValidatorBuilders = {
  string<state,key extends string | number | symbol>(key:key, errorMessage:() => string): StringValidatorBuilder<state,key>,
  number<state,key extends string | number | symbol>(key:key, errorMessage:() => string): NumberValidatorBuilder<state,key>,
  boolean<state,key extends string | number | symbol>(key:key, errorMessage:() => string): BoolValidatorBuilder<state,key>,
  Date<state,key extends string | number | symbol>(key:key, errorMessage:() => string): DateValidatorBuilder<state,key>,
  Object<state,t,key extends string | number | symbol>(key:key): ObjectValidatorBuilder<state,t,key>,
  // Generic<state,t,key extends string | number | symbol>(key:key, errorMessage:string): GenericValidatorBuilder<state,t,key>,
}

const baseValidatorBuilder = <state,t,key extends string | number | symbol>(key:key, errorMessage:() => string) : BaseValidatorBuilder<state,t,key> => ({
  fromPromise:(p:PromiseValidator<state,t>) => fromPromiseValidator(p, key.toString(), errorMessage),
  key:key
})

const delayed = (ms:number) => <a,>(value:a) => new Promise<a>((resolve, reject) => setTimeout(() => resolve(value), ms))

export const validatorBuilders : ValidatorBuilders = {
  Date:<state,key extends string | number | symbol>(key:key, errorMessage:() => string) => ({
    ...baseValidatorBuilder(key, errorMessage),
    eq:function(this:DateValidatorBuilder<state,key>, v:Date) {
      return this.fromPromise((state,value) => delayed(200)(value == undefined ? undefined : value.getDate() == v.getDate()))
    },
    ne:function(this:DateValidatorBuilder<state,key>, v:Date) { 
      return this.fromPromise((state,value) => delayed(200)(value == undefined ? undefined : value.getDate() != v.getDate())) 
    },
    gt:function(this:DateValidatorBuilder<state,key>, v:Date) { 
      return this.fromPromise((state,value) => delayed(200)(value == undefined ? undefined : value.getDate() > v.getDate())) 
    },
    lt:function(this:DateValidatorBuilder<state,key>, v:Date) { 
      return this.fromPromise((state,value) => delayed(200)(value == undefined ? undefined : value.getDate() < v.getDate())) 
    },
  }),
  boolean:<state,key extends string | number | symbol>(key:key, errorMessage:() => string) => ({
    ...baseValidatorBuilder(key, errorMessage),
    eq:function(this:BoolValidatorBuilder<state,key>, v:boolean) { 
      return this.fromPromise(async (state,value) => (value == undefined ? undefined : value == v)) 
    },
    ne:function(this:BoolValidatorBuilder<state,key>, v:boolean) { 
      return this.fromPromise(async (state,value) => (value == undefined ? undefined : value != v)) 
    },
    isTrue:function(this:BoolValidatorBuilder<state,key>) { 
      return this.eq(true)
    },
    isFalse:function(this:BoolValidatorBuilder<state,key>) { 
      return this.eq(false)
    },  
  }),
  number:<state,key extends string | number | symbol>(key:key, errorMessage:() => string) => ({
    ...baseValidatorBuilder(key, errorMessage),
    eq:function(this:NumberValidatorBuilder<state,key>, v:number) { 
      return this.fromPromise(async (state,value) => (value == undefined ? undefined : value == v)) 
    },
    ne:function(this:NumberValidatorBuilder<state,key>, v:number) { 
      return this.fromPromise(async (state,value) => (value == undefined ? undefined : value != v)) 
    },
    gt:function(this:NumberValidatorBuilder<state,key>, v:number) { 
      return this.fromPromise(async (state,value) => (value == undefined ? undefined : value > v)) 
    },
    lt:function(this:NumberValidatorBuilder<state,key>, v:number) { 
      return this.fromPromise(async (state,value) => (value == undefined ? undefined : value < v)) 
    },
  }),
  string:<state,key extends string | number | symbol>(key:key, errorMessage:() => string) => ({
    ...baseValidatorBuilder(key, errorMessage),
    matches:function(this:StringValidatorBuilder<state,key>, r:RegExp) { 
      return this.fromPromise(async(state,value) => (value == undefined ? undefined : r.test(value)))
    },
    notEmpty:function(this:StringValidatorBuilder<state,key>) { 
      return this.fromPromise(async(state,value) => (value == undefined ? undefined : value.length > 0)) 
    },
    isPhone:function(this:StringValidatorBuilder<state,key>) { 
      return this.matches(phoneNumberRegex())
    },
    isPostalCode:function(this:StringValidatorBuilder<state, key>) {
      return this.matches(/^(?:[1-9]\d{3} ?(?:[A-EGHJ-NPRTVWXZ][A-EGHJ-NPRSTVWXZ]|S[BCEGHJ-NPRTVWXZ]))$/i)
    },
    isEmail:function(this:StringValidatorBuilder<state,key>) { 
      return this.fromPromise(async(state,value) => (value == undefined || value.includes('.') && (value.includes('@') && (value.indexOf('.',value.indexOf('@')) > -1)))) 
    },
    isNumeric(this:StringValidatorBuilder<state, key>) {
      return this.matches(/\d+/)
    },
    eq:function(this:StringValidatorBuilder<state,key>, v:string) { 
      return this.fromPromise((state,value) => delayed(0)(value == undefined ? undefined : value == v)) 
    },
    ne:function(this:StringValidatorBuilder<state,key>, v:string) { 
      return this.fromPromise((state,value) => delayed(0)(value == undefined ? undefined : value != v)) 
    },
  }),
  Object:<state,t,key extends string | number | symbol>(key:key) => ({
    ...baseValidatorBuilder(key, () => ""),
    fields: function(this:ObjectValidatorBuilder<state,t,key>, fieldValidatorsBuilder:Partial<FieldValidatorsBuilder<state,t>>) : Validator<state,t> {
      let v : Validator<state,any> | undefined = undefined
      Object.keys(fieldValidatorsBuilder).forEach(k => {
        const [f, b] = (fieldValidatorsBuilder as any)[k]
        const v1 = f(b).map((([s,e]:[any,any]) => {
          // console.log(`Object selector in ${key}, narrowing ${JSON.stringify(e)}[${k}] -> ${JSON.stringify(e[k])}`)
          return e[k]
        }), (v:ValidatorResult) : ValidatorResult => v)
        v = v ? v.and(v1) : v1
      })
      return v != undefined ? (v as any).map(([s,e]:[any,any]) => e, (v:ValidatorResult) : ValidatorResult => validatorResultBuilders.nested(key.toString(), v)) 
             : yesValidator<state,any>(key.toString())
    },
  }),
  // Generic:<state,t,key extends string | number | symbol>(key:key, errorMessage:string) => 
  //   baseValidatorBuilder(key),
}

export type FieldValidators<state,entity> = { [ field in keyof entity ]:Validator<state,entity[field]> }
export type FieldValidatorsBuilder<state,entity> = { [ field in keyof entity ]:[Fun<ValidatorBuilder<state,entity[field],field>, Validator<state,entity[field]>>, ValidatorBuilder<state,entity[field],field>] }

export type EntityValidator<entity> = {
  fieldValidators:Partial<FieldValidators<entity,entity>>
}

export const buildValidator = <entity, >(fieldValidatorsBuilder:Partial<FieldValidatorsBuilder<entity,entity>>) : Validator<entity, entity> => {
  let v : Validator<entity,entity> | undefined = undefined
  Object.keys(fieldValidatorsBuilder).forEach(k => {
    const [f, b] = (fieldValidatorsBuilder as any)[k]
    const v1 = f(b).map((([s,e]:[any,any]) => {
      // console.log(`Root selector, narrowing ${JSON.stringify(e)}[${k}] -> ${JSON.stringify(e[k])}`)
      return e[k]
    }), (v:ValidatorResult) : ValidatorResult => v)
    v = v ? v.and(v1) : v1
  })
  return (v || yesValidator<entity, entity>(""))
}
